/**
 * SubscriptionProcessingPage.tsx
 * Updated to use real API calls and show progress for each step.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { api } from '../services/api';

const stepsKeys: Array<'creatingSubscription' | 'emailingDetails' | 'creatingUser'> = [
  'creatingSubscription',
  'emailingDetails',
  'creatingUser',
];

const SubscriptionProcessingPage: React.FC = () => {
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [userDialogInfo, setUserDialogInfo] = useState<{ email: string; password: string } | null>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const process = async () => {
      // Check if already completed
      const customerIdStr = sessionStorage.getItem('onboardingCustomerId');
      const customerId = customerIdStr ? parseInt(customerIdStr, 10) : null;
      if (!customerId) {
        setError('Customer ID not found');
        return;
      }

      const completed = sessionStorage.getItem('userCreationCompleted');
      if (completed === customerId.toString()) {
        setSuccess(true);
        return;
      }

      // Check if already processing
      if (isProcessing) return;
      setIsProcessing(true);

      try {
        // 1. Get plan, inventory, and customerId from storage
        const onboardingForm = localStorage.getItem('izypostOnboardingForm');
        let userEmail = '';
        let selectedPlanId = '';
        let inventoryByPlan: { [planId: string]: boolean } = {};
        let extraDevices = 0;
        if (onboardingForm) {
          try {
            const parsed = JSON.parse(onboardingForm);
            userEmail = parsed.email || '';
            selectedPlanId = parsed.selectedPlanId || '';
            inventoryByPlan = parsed.inventoryByPlan || {};
            extraDevices = parsed.extraDevices || 0;
          } catch {}
        }
        if (!userEmail || !selectedPlanId) throw new Error('Missing onboarding data.');

        // 2. Determine subscription type
        // These constants should match those in PlanSelectionPage
        const TABLE_SERVICE = "TS";
        const COUNTER_SERVICE = "CS";
        const TABLE_SERVICE_WITH_INVENTORY = "TS_WITH_INVENTORY";
        const COUNTER_SERVICE_WITH_INVENTORY = "CS_WITH_INVENTORY";
        const TABLE_SERVICE_ANNUAL = "TS_ANNUAL";
        const COUNTER_SERVICE_ANNUAL = "CS_ANNUAL";
        const TABLE_SERVICE_ANNUAL_WITH_INVENTORY = "TS_ANNUAL_WITH_INVENTORY";
        const COUNTER_SERVICE_ANNUAL_WITH_INVENTORY = "CS_ANNUAL_WITH_INVENTORY";
        const plans = [
          { id: 'monthly-standard', name: 'IZYPOS Counter Service', period: 'monthly' },
          { id: 'monthly-table', name: 'IZYPOS Table Service', period: 'monthly' },
          { id: 'annual-standard', name: 'IZYPOS Counter Service (Annual)', period: 'annual' },
          { id: 'annual-table', name: 'IZYPOS Table Service (Annual)', period: 'annual' },
        ];
        const selectedPlan = plans.find(p => p.id === selectedPlanId);
        if (!selectedPlan) throw new Error('Selected plan not found.');
        const withInventory = !!inventoryByPlan[selectedPlanId];
        let subscriptionType = '';
        if (selectedPlan.period === 'monthly') {
          if (selectedPlan.name.includes('Counter Service')) {
            subscriptionType = withInventory ? COUNTER_SERVICE_WITH_INVENTORY : COUNTER_SERVICE;
          } else if (selectedPlan.name.includes('Table Service')) {
            subscriptionType = withInventory ? TABLE_SERVICE_WITH_INVENTORY : TABLE_SERVICE;
          }
        } else if (selectedPlan.period === 'annual') {
          if (selectedPlan.name.includes('Counter Service')) {
            subscriptionType = withInventory ? COUNTER_SERVICE_ANNUAL_WITH_INVENTORY : COUNTER_SERVICE_ANNUAL;
          } else if (selectedPlan.name.includes('Table Service')) {
            subscriptionType = withInventory ? TABLE_SERVICE_ANNUAL_WITH_INVENTORY : TABLE_SERVICE_ANNUAL;
          }
        }
        if (!subscriptionType) throw new Error('Could not determine subscription type.');

        // 3. Create subscription
        setStep(0);
        const subscriptionPayload = {
          customerId,
          subscription: subscriptionType,
          oneTimeFee: true,
          extraDevices,
        };
        const subscriptionResponse = await api.createSubscription(subscriptionPayload);

        // 4. Email subscription
        setStep(1);
        await api.emailSubscription(subscriptionResponse.subscriptionId);

        // 5. Create user
        setStep(2);
        const izypostOnboardingForm = localStorage.getItem('izypostOnboardingForm');
        const merchantName = izypostOnboardingForm ? JSON.parse(izypostOnboardingForm).merchantFirstName + " " + JSON.parse(izypostOnboardingForm).merchantLastName : '';
        const userPayload = {
          customerId,
          name: merchantName,
          email: userEmail,
          password: 'Izy2025$'
        };
        await api.createUser(userPayload);

        const companyName = izypostOnboardingForm ? JSON.parse(izypostOnboardingForm).company : '';

        api.createMailingContact(userEmail,companyName,merchantName);
        
        // Mark as completed
        sessionStorage.setItem('userCreationCompleted', customerId.toString());
        
        setUserDialogInfo({ email: userEmail, password: 'Izy2025$' });
        setShowUserDialog(true);
        setSuccess(true);
      } catch (err: unknown) {
        setIsProcessing(false);
        setError((err && typeof err === 'object' && 'message' in err) ? (err as Error).message : 'An error occurred');
      }
    };
    process();
  }, []);

  if (error) {
    return (
      <div className="main-content" style={{textAlign: 'center'}}>
        <h1>{t('somethingWentWrong')}</h1>
        <p>{error}</p>
        <button className="btn" onClick={() => {
          localStorage.removeItem('izypostOnboardingForm');
          sessionStorage.removeItem('izypostOnboardingState');
          sessionStorage.removeItem('userCreationCompleted');
          window.location.href = '/';
        }}>{t('returnHome')}</button>
      </div>
    );
  }

  if (showUserDialog && userDialogInfo) {
    return (
      <div className="main-content" style={{textAlign: 'center'}}>
        <div className="user-dialog-overlay">
          <div className="user-dialog-box">
            <h2>{t('accountCreated')}</h2>
            <p><strong>{t('Username')}:</strong> {userDialogInfo.email}</p>
            <p><strong>{t('TemporaryPassword')}:</strong> {userDialogInfo.password}</p>
            <p style={{marginTop: 12}}>{t('checkYourEmailMessage')}</p>
            <button className="form-submit-btn" onClick={() => setShowUserDialog(false)}>Continue</button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="main-content" style={{textAlign: 'center'}}>
        <div style={{fontSize: '3rem', color: '#28a745', marginBottom: '1rem'}}>✓</div>
        <h1>{t('subscriptionComplete')}</h1>
        <p>{t('subscriptionProcessed')}</p>
        <p style={{color: '#007bff', fontWeight: 600, fontSize: '1.1rem', marginTop: '1.5rem'}}>{t('checkEmail')}</p>
        <button className="btn" onClick={() => {
          localStorage.removeItem('izypostOnboardingForm');
          sessionStorage.removeItem('izypostOnboardingState');
          sessionStorage.removeItem('userCreationCompleted');
          window.location.href = 'https://www.izyposmevweb.io';
        }}>{t('returnHome')}</button>
      </div>
    );
  }

  return (
    <div className="main-content" style={{textAlign: 'center'}}>
      <h1>{t('processingSubscription')}</h1>
      <div style={{margin: '2rem 0'}}>
        <div className="progress-bar" style={{height: '8px', background: '#e0e0e0', borderRadius: '4px', overflow: 'hidden', marginBottom: '1.5rem'}}>
          <div style={{width: `${((step+1)/stepsKeys.length)*100}%`, height: '100%', background: '#007bff', transition: 'width 0.5s'}} />
        </div>
        <div style={{fontSize: '1.1rem', color: '#007bff', fontWeight: 500}}>{t(stepsKeys[step] as keyof typeof import('../locales/en-US.json'))}</div>
      </div>
      <div className="spinner" style={{margin: '2rem auto', width: 48, height: 48, border: '5px solid #e0e0e0', borderTop: '5px solid #007bff', borderRadius: '50%', animation: 'spin 1s linear infinite'}} />
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default SubscriptionProcessingPage; 